"use client";

import {
  motion,
  type MotionValue,
  useScroll,
  useTransform,
} from "motion/react";
import { Fragment, useRef } from "react";

import { Magnetic } from "@/components/motion/magnetic";
import { Reveal } from "@/components/motion/reveal";
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";
import { Link } from "@/i18n/navigation";

import { CardRecto } from "./business-card";
import { CardStage } from "./business-card-3d/card-stage";
import { TIMELINE } from "./business-card-3d/choreography";

// =============================================================================

type StillHereProps = {
  title: string;
  role: string;
  tags: string[];
  cta: string;
  button: string;
  qrSvg: string;
  cardAlt: string;
};

const BUTTON_CLASS =
  "block rounded-lg border border-white/10 bg-surface-3 px-10 py-4 text-lg transition-colors duration-300 hover:bg-foreground hover:text-background";

function RevealWord({
  word,
  progress,
  range,
}: {
  word: string;
  progress: MotionValue<number>;
  range: [number, number];
}) {
  const opacity = useTransform(progress, range, [0.25, 1]);
  return (
    <motion.span style={{ opacity }} className="inline-block">
      {word}
    </motion.span>
  );
}

/**
 * "Still here?": the 3D business card falls while scrolling, flips from its
 * recto to its verso and lands below the call to action, which then reveals
 * itself word by word.
 */
export function StillHere({
  title,
  role,
  tags,
  cta,
  button,
  qrSvg,
  cardAlt,
}: StillHereProps) {
  const ref = useRef<HTMLElement>(null);
  const shouldReduceMotion = usePrefersReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });
  // JS-driven copy: the native scroll timeline mistracks in sticky layouts
  const progress = useTransform(scrollYProgress, (value) => value);

  const titleOpacity = useTransform(progress, [...TIMELINE.title], [1, 0]);
  const titleY = useTransform(progress, [...TIMELINE.title], ["0%", "-60%"]);
  const [ctaStart, ctaEnd] = TIMELINE.cta;
  const ctaOpacity = useTransform(
    progress,
    [ctaStart - 0.03, ctaStart],
    [0, 1]
  );
  const ctaY = useTransform(progress, [ctaStart - 0.03, ctaEnd], [32, 0]);
  const buttonOpacity = useTransform(progress, [...TIMELINE.button], [0, 1]);
  const buttonY = useTransform(progress, [...TIMELINE.button], [24, 0]);

  const words = cta.split(" ");

  // Keyboard focus on the hidden button: scroll to where it is revealed
  const revealCallToAction = () => {
    const section = ref.current;
    if (!section || progress.get() >= TIMELINE.button[1]) return;
    const end = section.getBoundingClientRect().bottom - window.innerHeight;
    window.scrollTo({ top: window.scrollY + end });
  };

  if (shouldReduceMotion) {
    return (
      <section
        ref={ref}
        className="container-page flex flex-col items-center gap-16 py-32 text-center"
      >
        <h2 className="text-title">{title}</h2>
        <div
          role="img"
          aria-label={cardAlt}
          className="@container relative aspect-1005/650 w-full max-w-xl"
        >
          <CardRecto role={role} />
        </div>
        <p className="text-[clamp(1.75rem,3.1vw,2.75rem)] leading-tight">
          {cta}
        </p>
        <Link href="/connect" className={BUTTON_CLASS}>
          {button}
        </Link>
      </section>
    );
  }

  return (
    <section
      ref={ref}
      data-section="still-here"
      className="relative h-[380svh]"
    >
      <div className="sticky top-0 h-svh overflow-hidden">
        {/* Title, covered by the card falling in front of it */}
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 px-5">
          <Reveal y={50}>
            <motion.h2
              className="text-center text-title"
              style={{ opacity: titleOpacity, y: titleY }}
            >
              {title}
            </motion.h2>
          </Reveal>
        </div>

        <CardStage
          className="z-10"
          progress={progress}
          role={role}
          tags={tags}
          qrSvg={qrSvg}
          label={cardAlt}
        />

        {/* Call to action, above the landed card */}
        <motion.div
          className="absolute inset-x-0 top-[15%] z-20 flex flex-col items-center gap-8 px-5 text-center md:top-[17%]"
          style={{ opacity: ctaOpacity, y: ctaY }}
        >
          {/* Inline words: the lines are balanced like the design */}
          <p className="max-w-[22ch] text-[clamp(1.75rem,3.1vw,2.75rem)] leading-[1.15] text-balance">
            {words.map((word, index) => {
              const start =
                ctaStart + (index / words.length) * (ctaEnd - ctaStart);
              return (
                <Fragment key={`${word}-${index}`}>
                  {index > 0 && " "}
                  <RevealWord
                    word={word}
                    progress={progress}
                    range={[start, start + (ctaEnd - ctaStart) / words.length]}
                  />
                </Fragment>
              );
            })}
          </p>
          <motion.div style={{ opacity: buttonOpacity, y: buttonY }}>
            <Magnetic>
              <Link
                href="/connect"
                className={BUTTON_CLASS}
                onFocus={revealCallToAction}
              >
                {button}
              </Link>
            </Magnetic>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

// =============================================================================
